import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { SECTION_GROUPS } from "@/components/sectionTypes";
import SectionRenderer from "@/components/SectionRenderer";
import { defaultSectionForSubtype } from "@/components/sectionTypes";
import { Sun, Moon, Sliders, Image as ImageIcon, AlertTriangle } from "lucide-react";
import ImagePickerModal from "@/components/ImagePickerModal";

// Palette values must stay in sync with SectionRenderer.jsx's `paletteMap`.
// Class strings are used by the swatch preview inside the field widget.
const PALETTE_OPTIONS = [
  { value: "sky",     label: "Sky",     swatch: "bg-sky-100 border-sky-300" },
  { value: "emerald", label: "Emerald", swatch: "bg-emerald-100 border-emerald-300" },
  { value: "amber",   label: "Amber",   swatch: "bg-amber-100 border-amber-300" },
  { value: "rose",    label: "Rose",    swatch: "bg-rose-100 border-rose-300" },
  { value: "violet",  label: "Violet",  swatch: "bg-violet-100 border-violet-300" },
  { value: "slate",   label: "Slate",   swatch: "bg-slate-100 border-slate-300" },
];

// Per-subtype content-defaults schema — mirrors the backend VE_DEFAULT_FIELDS
// whitelist. `type` is one of: text, textarea, url, url_image, number,
// select, bool, palette. Labels are user-facing.
const DEFAULT_SCHEMAS = {
  prose: [
    { key: "heading", label: "Default heading", type: "text" },
    { key: "html",    label: "Default body HTML", type: "textarea" },
  ],
  pearl: [
    { key: "heading", label: "Default heading", type: "text" },
    { key: "html",    label: "Default pearl HTML", type: "textarea" },
  ],
  tip: [
    { key: "heading", label: "Default heading", type: "text" },
    { key: "html",    label: "Default tip HTML", type: "textarea" },
  ],
  caution: [
    { key: "heading", label: "Default heading", type: "text" },
    { key: "html",    label: "Default caution HTML", type: "textarea" },
  ],
  summary_321: [
    { key: "heading", label: "Default heading", type: "text" },
    { key: "columns", label: "Columns", type: "select", numeric: true, options: [
      { value: "1", label: "1 — stacked" },
      { value: "2", label: "2 — side by side" },
      { value: "3", label: "3 — horizontal row" },
    ] },
    { key: "show_number", label: "Show counts in card titles", type: "bool" },
    { key: "label_insights", label: "Insights label", type: "text" },
    { key: "color_insights", label: "Insights colour", type: "palette" },
    { key: "label_actions",  label: "Actions label",  type: "text" },
    { key: "color_actions",  label: "Actions colour", type: "palette" },
    { key: "label_question", label: "Question label", type: "text" },
    { key: "color_question", label: "Question colour", type: "palette" },
  ],
  bibliography: [
    { key: "heading", label: "Default heading", type: "text" },
  ],
  tip_jar: [
    { key: "heading",      label: "Card heading",  type: "text" },
    { key: "body",         label: "Body copy",     type: "textarea" },
    { key: "button_label", label: "Button label",  type: "text" },
    { key: "allow_custom", label: "Allow visitors to enter a custom amount", type: "bool" },
  ],
  image: [
    { key: "heading",   label: "Default heading",   type: "text" },
    { key: "image_url", label: "Default image URL", type: "url_image" },
    { key: "image_alt", label: "Alt text",           type: "text" },
    { key: "caption",   label: "Caption",            type: "text" },
  ],
  image_and_text: [
    { key: "heading",        label: "Default heading",   type: "text" },
    { key: "image_url",      label: "Default image URL", type: "url_image" },
    { key: "image_alt",      label: "Alt text",          type: "text" },
    { key: "caption",        label: "Caption",           type: "text" },
    { key: "image_position", label: "Image position",    type: "select",
      options: [
        { value: "left",  label: "Left" },
        { value: "right", label: "Right" },
      ] },
    { key: "html", label: "Default body HTML", type: "textarea" },
  ],
  image_gallery: [
    { key: "heading", label: "Default heading", type: "text" },
  ],
  table: [
    { key: "heading", label: "Default heading", type: "text" },
    { key: "caption", label: "Default caption", type: "text" },
  ],
  graph: [
    { key: "heading", label: "Default heading", type: "text" },
    { key: "chart_type", label: "Chart type", type: "select", options: [
      { value: "bar",  label: "Bar" },
      { value: "line", label: "Line" },
      { value: "pie",  label: "Pie" },
    ] },
    { key: "caption", label: "Default caption", type: "text" },
  ],
  divider: [
    { key: "style",     label: "Line style",   type: "select",
      options: [
        { value: "solid",  label: "Solid" },
        { value: "dashed", label: "Dashed" },
        { value: "dotted", label: "Dotted" },
        { value: "double", label: "Double" },
      ] },
    { key: "thickness", label: "Thickness",    type: "select",
      options: [
        { value: "thin",   label: "Thin" },
        { value: "medium", label: "Medium" },
        { value: "thick",  label: "Thick" },
      ] },
    { key: "color",  label: "Color",   type: "text" },
    { key: "width",  label: "Width",   type: "select",
      options: [
        { value: "full",   label: "Full" },
        { value: "wide",   label: "Wide" },
        { value: "narrow", label: "Narrow" },
        { value: "tight",  label: "Tight" },
      ] },
    { key: "align",  label: "Align",   type: "select",
      options: [
        { value: "left",   label: "Left" },
        { value: "center", label: "Center" },
        { value: "right",  label: "Right" },
      ] },
    { key: "label",  label: "Optional label", type: "text" },
    { key: "margin", label: "Margin", type: "select",
      options: [
        { value: "sm", label: "Small" },
        { value: "md", label: "Medium" },
        { value: "lg", label: "Large" },
        { value: "xl", label: "Extra large" },
      ] },
  ],
  layout_grid: [
    { key: "heading", label: "Default heading", type: "text" },
    { key: "preset", label: "Preset",         type: "text" },
    { key: "columns", label: "Columns",        type: "number" },
    { key: "gap",     label: "Gap",            type: "select",
      options: [
        { value: "sm", label: "Small" },
        { value: "md", label: "Medium" },
        { value: "lg", label: "Large" },
      ] },
    { key: "align_items", label: "Align items", type: "select",
      options: [
        { value: "stretch", label: "Stretch" },
        { value: "start",   label: "Top" },
        { value: "center",  label: "Center" },
        { value: "end",     label: "Bottom" },
      ] },
  ],
  hero: [
    { key: "heading",  label: "Default heading", type: "text" },
    { key: "layout",   label: "Layout", type: "select",
      options: [
        { value: "image-right",  label: "Image right" },
        { value: "image-left",   label: "Image left" },
        { value: "image-top",    label: "Image top" },
        { value: "image-bottom", label: "Image bottom" },
        { value: "text-only",    label: "Text only" },
        { value: "image-only",   label: "Image only" },
        { value: "centered",     label: "Centered" },
        { value: "overlay",      label: "Overlay" },
      ] },
    { key: "eyebrow",     label: "Eyebrow",           type: "text" },
    { key: "title",       label: "Title",             type: "text" },
    { key: "description", label: "Description",       type: "textarea" },
    { key: "description_html", label: "Description HTML", type: "textarea" },
    { key: "text_align",  label: "Text align",        type: "select",
      options: [
        { value: "left",   label: "Left" },
        { value: "center", label: "Center" },
        { value: "right",  label: "Right" },
      ] },
    { key: "title_color", label: "Title color", type: "text" },
    { key: "subtext_color", label: "Subtext color", type: "text" },
    { key: "cta_label",   label: "Primary CTA label", type: "text" },
    { key: "cta_href",    label: "Primary CTA href",  type: "text" },
    { key: "cta_style",   label: "Primary CTA style", type: "select",
      options: [
        { value: "primary",   label: "Primary" },
        { value: "secondary", label: "Secondary" },
        { value: "ghost",     label: "Ghost" },
      ] },
    { key: "cta2_label",  label: "Secondary CTA label", type: "text" },
    { key: "cta2_href",   label: "Secondary CTA href",  type: "text" },
    { key: "cta2_style",  label: "Secondary CTA style", type: "select",
      options: [
        { value: "primary",   label: "Primary" },
        { value: "secondary", label: "Secondary" },
        { value: "ghost",     label: "Ghost" },
      ] },
    { key: "image_url",   label: "Hero image URL",   type: "url_image" },
    { key: "image_alt",   label: "Hero image alt",   type: "text" },
  ],
  pathways_grid:   [
    { key: "heading",     label: "Default heading", type: "text" },
    { key: "eyebrow",     label: "Eyebrow",         type: "text" },
    { key: "description", label: "Description",     type: "textarea" },
  ],
  recent_blogs: [
    { key: "heading", label: "Default heading", type: "text" },
    { key: "limit",   label: "Max cards",       type: "number" },
  ],
  glossary_teaser: [
    { key: "heading",     label: "Default heading", type: "text" },
    { key: "eyebrow",     label: "Eyebrow",         type: "text" },
    { key: "description", label: "Description",     type: "textarea" },
    { key: "cta_label",   label: "CTA label",       type: "text" },
    { key: "cta_href",    label: "CTA href",        type: "text" },
  ],
};

const DefaultsFieldset = ({ subtype, defaults, onChange }) => {
  const schema = DEFAULT_SCHEMAS[subtype];
  const [pickerFor, setPickerFor] = useState(null); // key of the field currently picking
  if (!schema) return null;
  const val = (k) => (defaults?.[k] ?? "");
  const set = (k, v) => onChange({ ...(defaults || {}), [k]: v });
  return (
    <div
      data-testid={`ve-defaults-${subtype}`}
      className="mt-4 border-t border-dashed border-slate-200 pt-3 space-y-2"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500">
        <Sliders className="w-3 h-3"/>
        Defaults for new sections
      </div>
      <div className="grid md:grid-cols-2 gap-2">
        {schema.map(f => (
          <div key={f.key} className={`flex flex-col gap-1 ${f.type === "bool" ? "md:col-span-2" : ""}`}>
            {f.type !== "bool" && (
              <label className="text-[10px] uppercase tracking-widest text-slate-400">{f.label}</label>
            )}
            {f.type === "textarea" ? (
              <textarea
                data-testid={`ve-defaults-${subtype}-${f.key}`}
                value={val(f.key)}
                onChange={e => set(f.key, e.target.value)}
                rows={f.key === "description_html" || f.key === "html" ? 3 : 2}
                className="text-xs border border-slate-200 rounded px-2 py-1.5 font-mono"
              />
            ) : f.type === "select" ? (
              <Select
                value={val(f.key) !== "" ? String(val(f.key)) : undefined}
                onValueChange={v => set(f.key, f.numeric ? Number(v) : v)}
              >
                <SelectTrigger data-testid={`ve-defaults-${subtype}-${f.key}`} className="h-8 text-xs">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {f.options.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : f.type === "palette" ? (
              <div
                data-testid={`ve-defaults-${subtype}-${f.key}`}
                role="radiogroup"
                className="flex flex-wrap gap-1.5"
              >
                {PALETTE_OPTIONS.map(o => {
                  const active = val(f.key) === o.value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      data-testid={`ve-defaults-${subtype}-${f.key}-${o.value}`}
                      role="radio"
                      aria-checked={active}
                      title={o.label}
                      onClick={() => set(f.key, o.value)}
                      className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${o.swatch} ${
                        active ? "ring-2 ring-offset-2 ring-slate-900 scale-110" : "border-transparent"
                      }`}
                    >
                      <span className="sr-only">{o.label}</span>
                    </button>
                  );
                })}
              </div>
            ) : f.type === "bool" ? (
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  data-testid={`ve-defaults-${subtype}-${f.key}`}
                  checked={val(f.key) === true}
                  onCheckedChange={v => set(f.key, !!v)}
                />
                <span className="text-[11px] text-slate-600">{f.label}</span>
              </label>
            ) : f.type === "url_image" ? (
              <div className="flex gap-1">
                <Input
                  data-testid={`ve-defaults-${subtype}-${f.key}`}
                  type="text"
                  value={val(f.key)}
                  onChange={e => set(f.key, e.target.value)}
                  className="h-8 text-xs flex-1"
                  placeholder="https://…"
                />
                <button
                  type="button"
                  data-testid={`ve-defaults-${subtype}-${f.key}-pick`}
                  onClick={() => setPickerFor(f.key)}
                  title="Pick from library or upload"
                  className="shrink-0 h-8 px-2 rounded border border-slate-300 hover:bg-slate-50 text-slate-600 inline-flex items-center gap-1 text-xs"
                >
                  <ImageIcon className="w-3.5 h-3.5"/>
                  Pick
                </button>
              </div>
            ) : (
              <Input
                data-testid={`ve-defaults-${subtype}-${f.key}`}
                type={f.type === "number" ? "number" : "text"}
                value={val(f.key)}
                onChange={e => set(f.key, f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
                className="h-8 text-xs"
              />
            )}
          </div>
        ))}
      </div>
      <div className="text-[10px] text-slate-400 leading-relaxed">
        These values seed new sections as they're added inside the blog editor. Existing sections are never changed.
      </div>
      <ImagePickerModal
        open={!!pickerFor}
        onClose={() => setPickerFor(null)}
        onPick={(url) => {
          if (pickerFor) set(pickerFor, url);
          setPickerFor(null);
        }}
      />
    </div>
  );
};

// A small color input row; native <input type="color"> requires #rrggbb, so
// we fall back to a text field when the current value isn't a valid hex
// (e.g. "transparent") to still let admins edit it.
const isHex = (v) => typeof v === "string" && /^#[0-9a-f]{6}$/i.test(v);
const ColorField = ({ label, value, onChange, allowTransparent }) => (
  <div className="flex items-center gap-2">
    <label className="text-xs uppercase tracking-widest text-slate-500 w-24 shrink-0">{label}</label>
    {isHex(value) ? (
      <input type="color" value={value} onChange={e => onChange(e.target.value)}
             className="h-8 w-10 rounded border border-slate-200 bg-white cursor-pointer"/>
    ) : (
      <div className="h-8 w-10 rounded border border-dashed border-slate-300 bg-white grid place-items-center text-[9px] text-slate-400">none</div>
    )}
    <Input value={value || ""} onChange={e => onChange(e.target.value)} className="h-8 text-xs flex-1"/>
    {allowTransparent && (
      <button
        onClick={() => onChange("transparent")}
        className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-slate-900 border border-slate-200 rounded px-2 py-1">
        None
      </button>
    )}
  </div>
);

const StyleCard = ({ meta, cell, onChange }) => {
  const [mode, setMode] = useState("light"); // "light" | "dark"
  // The effective cell being edited: when in dark mode we edit cell.dark
  // (falling back to the light values when not set yet).
  const editing = mode === "dark"
    ? { ...cell, ...(cell?.dark || {}) }
    : cell;
  const set = (patch) => {
    if (mode === "dark") {
      onChange({ ...cell, dark: { ...(cell?.dark || {}), ...patch } });
    } else {
      onChange({ ...cell, ...patch });
    }
  };
  const Icon = meta.Icon;
  // Build a demo section for the mini-preview. We pass a single-cell VE shim
  // so `defaultSectionForSubtype` merges the live-edited `defaults` into the
  // seed — this is what makes typing a new hero image URL (or CTA copy, etc.)
  // update the preview in real time.
  const previewVe = { [meta.type]: { [meta.subtype]: cell } };
  const def = defaultSectionForSubtype(meta.subtype, previewVe);
  const isImageSubtype = meta.subtype.includes("image");
  const demoImageUrl = isImageSubtype
    ? (def.data.image_url || "https://images.unsplash.com/photo-1520975916090-3105956dac38?auto=format&fit=crop&w=800&q=60")
    : undefined;
  // For subtypes whose renderer shows an "Empty …" placeholder when the
  // content arrays are unset (summary_321, bibliography, table, graph),
  // seed sample content so the admin can actually SEE how their color /
  // label / column choices look in the preview. When admins populate real
  // content in the editor, this fallback is bypassed.
  const summarySeed = meta.subtype === "summary_321"
    ? {
        insights: (def.data.insights && def.data.insights.filter(Boolean).length > 0)
          ? def.data.insights
          : ["Sample insight one", "Sample insight two", "Sample insight three"],
        actions: (def.data.actions && def.data.actions.filter(Boolean).length > 0)
          ? def.data.actions
          : ["Take an action here", "Then act on this too"],
        question: def.data.question || "What would make today a good day?",
      }
    : {};
  const bibliographySeed = meta.subtype === "bibliography"
    ? { items: (def.data.items && def.data.items.filter(Boolean).length > 0)
        ? def.data.items
        : ["Author, A. (2024). Sample reference title. Journal of Examples, 12(3), 45-67.",
           "Doe, J. (2023). Another sample source. Example Press."] }
    : {};
  const tableSeed = meta.subtype === "table"
    ? {
        columns: (def.data.columns && def.data.columns.length > 0) ? def.data.columns : ["Column 1", "Column 2"],
        rows: (def.data.rows && def.data.rows.length > 0) ? def.data.rows : [["Row 1", "Value 1"], ["Row 2", "Value 2"]],
      }
    : {};
  const graphSeed = meta.subtype === "graph"
    ? { series: (def.data.series && def.data.series.length > 0)
        ? def.data.series
        : [{ label: "A", value: 12 }, { label: "B", value: 24 }, { label: "C", value: 18 }] }
    : {};
  const demo = {
    id: `demo-${meta.subtype}`, level: 2,
    heading: def.heading || (meta.subtype === "prose" ? "Heading" : meta.label),
    html: def.html?.length > 5 ? def.html : "<p>Preview text goes here — this is how your callout or prose will render on the reader.</p>",
    type: meta.type, subtype: meta.subtype,
    data: {
      ...def.data,
      caption: (isImageSubtype || meta.subtype === "table" || meta.subtype === "graph")
        ? (def.data.caption || "Caption")
        : def.data.caption,
      image_url: demoImageUrl !== undefined ? demoImageUrl : def.data.image_url,
      images: meta.subtype === "image_gallery"
        ? [
          { url: "https://images.unsplash.com/photo-1520975916090-3105956dac38?auto=format&fit=crop&w=400&q=60", caption: "A" },
          { url: "https://images.unsplash.com/photo-1497436072909-60f360e1d4b1?auto=format&fit=crop&w=400&q=60", caption: "B" },
          { url: "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=400&q=60", caption: "C" },
        ]
        : def.data.images,
      ...summarySeed,
      ...bibliographySeed,
      ...tableSeed,
      ...graphSeed,
    },
  };
  return (
    <div data-testid={`ve-card-${meta.subtype}`} className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 bg-slate-50">
        <Icon className="w-4 h-4 text-sky-700"/>
        <div className="font-heading font-semibold text-slate-900">{meta.label}</div>
        <span className="text-xs text-slate-500">— {meta.hint}</span>
        <div className="ml-auto inline-flex rounded-full border border-slate-200 overflow-hidden text-xs">
          <button
            data-testid={`ve-mode-light-${meta.subtype}`}
            onClick={() => setMode("light")}
            className={`px-2.5 py-1 inline-flex items-center gap-1 transition-colors ${mode === "light" ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:text-slate-900"}`}>
            <Sun className="w-3 h-3"/>Light
          </button>
          <button
            data-testid={`ve-mode-dark-${meta.subtype}`}
            onClick={() => setMode("dark")}
            className={`px-2.5 py-1 inline-flex items-center gap-1 transition-colors ${mode === "dark" ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:text-slate-900"}`}>
            <Moon className="w-3 h-3"/>Dark
          </button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-6 p-4">
        {/* Controls */}
        <div className="space-y-2.5">
          <ColorField label="Background" value={editing?.bg} onChange={v => set({ bg: v })} allowTransparent/>
          <ColorField label="Text" value={editing?.text} onChange={v => set({ text: v })}/>
          <ColorField label="Accent" value={editing?.accent} onChange={v => set({ accent: v })}/>
          <ColorField label="Border" value={editing?.border} onChange={v => set({ border: v })} allowTransparent/>
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase tracking-widest text-slate-500 w-24 shrink-0">Align</label>
            <Select value={editing?.align} onValueChange={v => set({ align: v })}>
              <SelectTrigger className="h-8 flex-1 text-xs"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="left">Left</SelectItem>
                <SelectItem value="center">Center</SelectItem>
                <SelectItem value="right">Right</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase tracking-widest text-slate-500 w-24 shrink-0">Radius</label>
            <Input type="number" min="0" max="32" value={editing?.radius ?? 8}
                   onChange={e => set({ radius: Math.max(0, Math.min(32, Number(e.target.value)||0)) })}
                   className="h-8 flex-1 text-xs"/>
            <span className="text-xs text-slate-400">px</span>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase tracking-widest text-slate-500 flex-1">Bold heading</label>
            <Switch checked={editing?.heading_bold} onCheckedChange={v => set({ heading_bold: v })}/>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase tracking-widest text-slate-500 flex-1">Show icon</label>
            <Switch checked={editing?.show_icon} onCheckedChange={v => set({ show_icon: v })}/>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase tracking-widest text-slate-500 flex-1">Show caption</label>
            <Switch checked={editing?.show_caption} onCheckedChange={v => set({ show_caption: v })}/>
          </div>
          {/* Content defaults — subtype-level, so shown in BOTH light and
              dark preview modes. (The per-card mode slider affects only the
              live preview + which set of visual style controls appears
              above, not these persistent content defaults.) */}
          {DEFAULT_SCHEMAS[meta.subtype] && (
            <DefaultsFieldset
              subtype={meta.subtype}
              defaults={cell?.defaults}
              onChange={(d) => onChange({ ...cell, defaults: d })}
            />
          )}
        </div>
        {/* Preview — respects the current Light/Dark toggle regardless of
           site theme. Adds `class="dark"` when previewing Dark so Tailwind's
           dark variants activate inside; adds `class="force-light"` when
           previewing Light so the outer `<html class="dark">` (site theme)
           does NOT leak dark: styles into a light preview. The
           `force-light` opt-out is defined in tailwind.config.js. */}
        <div
          className={`${mode === "dark" ? "dark" : "force-light"} border border-dashed border-slate-200 rounded-md p-4 min-h-[200px] blog-content ${
            mode === "dark" ? "bg-slate-950 border-slate-800" : "bg-white"
          }`}
        >
          <div className={`text-[10px] uppercase tracking-widest mb-2 ${mode === "dark" ? "text-slate-400" : "text-slate-500"}`}>
            Preview · {mode === "dark" ? "Dark mode" : "Light mode"}
          </div>
          <SectionRenderer section={demo} veSettings={previewVe} forceMode={mode} preview/>
        </div>
      </div>
    </div>
  );
};

export default function VisualEditorPanel() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { api.get("/site-settings/visual-editor").then(r => setSettings(r.data)); }, []);

  const setCell = (group, sub, cell) => {
    setSettings(prev => ({ ...prev, [group]: { ...(prev?.[group] || {}), [sub]: cell } }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/site-settings/visual-editor", settings);
      setSettings(r.data);
      setDirty(false);
      toast.success("Visual Editor styles saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!settings) return <div className="p-6 text-slate-500">Loading…</div>;

  return (
    <div data-testid="visual-editor-panel" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-heading text-xl font-semibold text-slate-900">Global Section Editor</div>
          <div className="text-sm text-slate-500">Global styles applied to every blog for each section subtype.</div>
        </div>
        <Button data-testid="ve-save" onClick={save} disabled={saving || !dirty} className="bg-sky-700 hover:bg-sky-600">
          {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </Button>
      </div>

      {SECTION_GROUPS.map(g => (
        <section key={g.type} className="space-y-3">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{g.label}</div>
          <div className="space-y-4">
            {g.items.map(it => (
              <StyleCard
                key={it.subtype}
                meta={{ ...it, type: g.type }}
                cell={settings[g.type]?.[it.subtype]}
                onChange={cell => setCell(g.type, it.subtype, cell)}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
